import { Controller } from "@hotwired/stimulus"

// Renders a hidden <select multiple> as Veridian clickable chips matching the
// mockup. The native select stays the form's data source (selected <option>s
// submit as name[]). An optional "+ more" toggle reveals a search over the full
// option list so the default view matches the mockup while all options stay
// reachable.
//   data-chip-select-curated-value: JSON array of [value, label] pairs (or bare
//     values) always shown as chips. Missing options are created so the chip
//     renders and submits.
//   data-chip-select-icons-value:   JSON object {value: "material_symbol_name"}.
export default class extends Controller {
  static targets = ["select", "chips", "search", "results", "searchWrap"]
  static values = { curated: Array, icons: Object }

  connect() {
    this._ensureCuratedOptions()
    this.options = Array.from(this.selectTarget.options).map((o) => ({ value: o.value, label: o.text }))
    this._renderChips()
  }

  // Ensure every curated value exists as an <option> so its chip renders and
  // submits; record the mockup label for each so the chip shows it even when the
  // native <option> text differs.
  _ensureCuratedOptions() {
    this.labelOverride = {}
    if (!this.hasCuratedValue) return
    const have = new Set(Array.from(this.selectTarget.options).map((o) => o.value))
    for (const entry of this.curatedValue) {
      const [value, label] = Array.isArray(entry) ? entry : [entry, entry]
      if (label) this.labelOverride[value] = label
      if (!have.has(value)) {
        const opt = document.createElement("option")
        opt.value = value
        opt.text = label || value
        this.selectTarget.appendChild(opt)
        have.add(value)
      }
    }
  }

  _curatedValues() {
    return this.hasCuratedValue ? this.curatedValue.map((e) => (Array.isArray(e) ? e[0] : e)) : []
  }

  _renderChips() {
    const shown = []
    const seen = new Set()
    const push = (v) => { if (seen.has(v)) return; const o = this._byValue(v); if (o) { shown.push(o); seen.add(v) } }
    this._curatedValues().forEach(push)
    this._selectedValues().forEach(push)
    this.chipsTarget.innerHTML = shown.map((o) => this._chipHtml(o)).join("")
  }

  _chipHtml(o) {
    const on = this._isSelected(o.value)
    const label = (this.labelOverride && this.labelOverride[o.value]) || o.label
    const icon = (this.hasIconsValue && this.iconsValue[o.value])
      ? `<span class="material-symbols-outlined text-[14px]" aria-hidden="true">${this.iconsValue[o.value]}</span> `
      : ""
    const cls = on
      ? "bg-primary-container text-on-primary-container border-primary-container font-medium"
      : "border-outline-variant text-on-surface-variant hover:bg-surface-container-high"
    return `<button type="button" data-action="chip-select#toggle" data-value="${this._esc(o.value)}" aria-pressed="${on}" class="px-3 py-1 rounded-full border font-body-sm text-body-sm inline-flex items-center gap-1 transition-colors ${cls}">${icon}${this._esc(label)}</button>`
  }

  toggle(e) {
    this._setSelected(e.currentTarget.getAttribute("data-value"), null)
    this._renderChips()
  }

  toggleMore() {
    if (!this.hasSearchWrapTarget) return
    this.searchWrapTarget.classList.toggle("hidden")
    if (!this.searchWrapTarget.classList.contains("hidden") && this.hasSearchTarget) this.searchTarget.focus()
  }

  search(e) {
    if (!this.hasResultsTarget) return
    const q = (e.target.value || "").toLowerCase().trim()
    if (!q) { this.resultsTarget.innerHTML = ""; this.resultsTarget.classList.add("hidden"); return }
    const matches = this.options
      .filter((o) => o.label.toLowerCase().includes(q) && !this._isSelected(o.value))
      .slice(0, 8)
    this.resultsTarget.innerHTML = matches.length
      ? matches.map((o) => `<button type="button" data-action="chip-select#pick" data-value="${this._esc(o.value)}" class="block w-full text-left px-3 py-2 font-body-sm text-body-sm text-on-surface hover:bg-surface-container-high">${this._esc(o.label)}</button>`).join("")
      : `<p class="px-3 py-2 font-body-sm text-body-sm text-text-muted">No matches</p>`
    this.resultsTarget.classList.remove("hidden")
  }

  pick(e) {
    this._setSelected(e.currentTarget.getAttribute("data-value"), true)
    if (this.hasSearchTarget) this.searchTarget.value = ""
    if (this.hasResultsTarget) { this.resultsTarget.innerHTML = ""; this.resultsTarget.classList.add("hidden") }
    this._renderChips()
  }

  _setSelected(value, force) {
    const opt = Array.from(this.selectTarget.options).find((o) => o.value === value)
    if (!opt) return
    opt.selected = force === null ? !opt.selected : force
    this.selectTarget.dispatchEvent(new Event("change", { bubbles: true }))
  }

  _selectedValues() { return Array.from(this.selectTarget.selectedOptions).map((o) => o.value) }
  _isSelected(v) { const o = Array.from(this.selectTarget.options).find((x) => x.value === v); return o ? o.selected : false }
  _byValue(v) { return this.options.find((o) => o.value === v) }
  _esc(s) { const d = document.createElement("div"); d.textContent = s == null ? "" : String(s); return d.innerHTML }
}
